import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import apiFetch from "../api";

export default function ApiTokens() {
  const toast = useToast();
  const [tokens, setTokens] = useState([]);
  const [name, setName] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiFetch("/tokens");
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Could not load tokens");
      setTokens(body.tokens || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load tokens");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createToken = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: "Name is required", status: "warning", duration: 3000 });
      return;
    }
    setCreating(true);
    setError("");
    try {
      const payload = { name: trimmed };
      if (expiresAt) payload.expires_at = new Date(expiresAt).toISOString();
      const res = await apiFetch("/tokens", {
        method: "POST",
        body: JSON.stringify(payload),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Could not create token");
      setRevealed(body);
      setName("");
      setExpiresAt("");
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create token");
    } finally {
      setCreating(false);
    }
  };

  const revoke = async (id) => {
    setError("");
    try {
      const res = await apiFetch(`/tokens/${encodeURIComponent(id)}`, { method: "DELETE" });
      const body = await res.json();
      if (!res.ok) throw new Error(body.detail || "Could not revoke token");
      if (revealed?.id === id) setRevealed(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not revoke token");
    }
  };

  const copyRevealed = async () => {
    if (!revealed?.token) return;
    try {
      await navigator.clipboard.writeText(revealed.token);
      toast({ title: "Copied to clipboard", status: "success", duration: 2000 });
    } catch {
      toast({ title: "Copy failed — select the token manually", status: "warning", duration: 4000 });
    }
  };

  return (
    <Stack spacing={5} w="100%">
      <Box>
        <Heading size="md" mb={1}>
          API tokens
        </Heading>
        <Text fontSize="sm" color="gray.600">
          Long-lived tokens for assistants and scripts. Use{" "}
          <Text as="span" fontFamily="mono">
            Authorization: Bearer …
          </Text>{" "}
          on every /api/v1 call, same as your browser session.
        </Text>
      </Box>

      {error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {error}
        </Alert>
      ) : null}

      {revealed ? (
        <Alert status="success" borderRadius="md" flexDirection="column" alignItems="stretch">
          <AlertIcon alignSelf="flex-start" />
          <Text fontWeight="semibold" mt={2}>
            Copy this token now — it will not be shown again
          </Text>
          <Text fontFamily="mono" fontSize="sm" wordBreak="break-all" bg="whiteAlpha.800" p={2} borderRadius="md" mt={2}>
            {revealed.token}
          </Text>
          <Button size="sm" alignSelf="flex-start" mt={3} onClick={copyRevealed}>
            Copy token
          </Button>
        </Alert>
      ) : null}

      <Stack spacing={3} p={4} borderWidth="1px" borderRadius="md" borderColor="gray.200">
        <Text fontWeight="semibold">Create token</Text>
        <FormControl>
          <FormLabel fontSize="sm">Name</FormLabel>
          <Input
            size="sm"
            placeholder="Grok Bot"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel fontSize="sm">Expiry (optional)</FormLabel>
          <Input
            size="sm"
            type="datetime-local"
            value={expiresAt}
            onChange={(event) => setExpiresAt(event.target.value)}
          />
        </FormControl>
        <Button size="sm" colorScheme="gray" isLoading={creating} onClick={createToken}>
          Create token
        </Button>
      </Stack>

      <Box>
        <Text fontWeight="semibold" mb={2}>
          Your tokens
        </Text>
        {loading ? (
          <Text fontSize="sm" color="gray.500">
            Loading…
          </Text>
        ) : tokens.length === 0 ? (
          <Text fontSize="sm" color="gray.500">
            No active tokens yet.
          </Text>
        ) : (
          <Stack spacing={2}>
            {tokens.map((token) => (
              <Flex
                key={token.id}
                justify="space-between"
                align="center"
                gap={3}
                p={3}
                borderWidth="1px"
                borderRadius="md"
                borderColor="gray.100"
                flexWrap="wrap"
              >
                <Box minW={0}>
                  <Text fontWeight="medium">{token.name}</Text>
                  <Text fontSize="xs" color="gray.500">
                    Created {new Date(token.created_at).toLocaleString()}
                    {token.expires_at
                      ? ` · expires ${new Date(token.expires_at).toLocaleString()}`
                      : " · no expiry"}
                  </Text>
                </Box>
                <Button size="sm" variant="outline" colorScheme="red" onClick={() => revoke(token.id)}>
                  Revoke
                </Button>
              </Flex>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
